import { useEffect, useRef, useState } from 'react'

type InlineEditableProps = {
  value: string
  onSave: (next: string) => Promise<void>
  placeholder?: string
  renderValue?: (val: string) => React.ReactNode
  saveOnBlur?: boolean
  disabled?: boolean
  item?: React.ReactNode
  icon?: React.ReactNode
  maxLength?: number
  showCounter?: boolean
}

export function InlineEditable({
  value,
  onSave,
  placeholder = '',
  renderValue,
  saveOnBlur = false,
  disabled = false,
  item,
  icon,
  maxLength,
  showCounter,
}: InlineEditableProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  // If external value changes, sync draft (to avoid external updates out of sync)
  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const commit = async () => {
    if (draft !== value) await onSave(draft.trim())
    setEditing(false)
  }
  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  if (disabled) {
    return <span className="t-sm text-primary">{renderValue ? renderValue(value) : value}</span>
  }

  return (
    <span className="group relative inline-flex items-center gap-2">
      {!editing ? (
        <>
          <span className="t-sm text-primary">
            {renderValue
              ? renderValue(value)
              : value || <span className="text-secondary">{placeholder}</span>}
          </span>

          {/* Edit button that appears on hover: put it in the same group, hovering it won't close hover */}
          <button
            type="button"
            className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition
                       t-sm text-highlight inline-flex items-center gap-1"
            onClick={() => setEditing(true)}
          >
            {icon}
            {item}
          </button>
        </>
      ) : (
        <span className="inline-flex flex-col gap-1">
          <span className="inline-flex items-center gap-2">
            <input
              ref={inputRef}
              className="h-9 w-24 px-3 rounded-md border border-neutral-200 outline-none
                         focus:border-primary t-sm text-primary"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit()
                if (e.key === 'Escape') cancel()
              }}
              onBlur={() => {
                if (saveOnBlur) commit()
              }}
              placeholder={placeholder}
            />
            <button type="button" onClick={commit} className="t-sm text-btn-primary">
              Save
            </button>
            <button type="button" onClick={cancel} className="t-sm text-secondary">
              Cancel
            </button>
          </span>
          {maxLength && (showCounter ?? true) && (
            <span
              className={`t-xs mb-1 ${
                draft.length > maxLength
                  ? 'text-error'
                  : draft.length === maxLength
                    ? 'text-primary'
                    : 'text-tertiary'
              }`}
            >
              {draft.length} / {maxLength}
            </span>
          )}
        </span>
      )}
    </span>
  )
}
